/**
 * ListState — why a table is empty, said plainly.
 *
 * Until iter 639 every school list rendered one sentence for six different
 * situations: `data?.students || []` leaves an empty array whether the records
 * do not exist, the filters excluded them, the request 403'd or the server
 * fell over — and the count beside the heading read `data?.total || 0`, which
 * is 0 for all of them too. A school office looking at "No students found"
 * could not tell a genuinely empty register from a failed request, and neither
 * could we when it was reported.
 *
 * Six states, each with the action that actually helps:
 *   loading | could not load (retry) | not permitted | session expired
 *   | no match for these filters (clear) | genuinely nothing here
 */
import React from 'react';
import { Loader2, AlertTriangle, Lock, FilterX, Inbox, RotateCw, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';

const statusOf = (error) => error?.status || error?.cause?.status || 0;

export const listStateOf = ({ isLoading, isFetching, error, count, filtersActive }) => {
  if (isLoading) return 'loading';
  if (error) {
    const status = statusOf(error);
    if (status === 401) return 'signed-out';
    if (status === 403) return 'forbidden';
    return 'failed';
  }
  if (count > 0) return 'ready';
  return filtersActive ? 'filtered-out' : 'empty';
};

/**
 * `noun` is the plural the office uses — "students", "teachers". `onClear`
 * and `onRetry` are offered only when they can actually change the outcome.
 */
export const ListState = ({
  isLoading, isFetching, error, count = 0, filtersActive = false,
  noun = 'records', onClear, onRetry, testId = 'list-state', compact = false,
  filterSummary = [],
}) => {
  const state = listStateOf({ isLoading, isFetching, error, count, filtersActive });
  if (state === 'ready') return null;

  const shell = (icon, title, detail, action) => (
    <div
      className={`flex flex-col items-center justify-center gap-3 text-center ${compact ? 'py-8' : 'py-14'}`}
      data-testid={`${testId}-${state}`}
      role={state === 'failed' || state === 'forbidden' ? 'alert' : undefined}
    >
      {icon}
      <div>
        <p className="text-sm font-medium text-slate-700">{title}</p>
        {detail && <p className="mt-1 text-xs text-slate-500">{detail}</p>}
      </div>
      {action}
    </div>
  );

  if (state === 'loading') {
    return shell(
      <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />,
      `Loading ${noun}\u2026`, null, null,
    );
  }

  if (state === 'failed') {
    // The server's own message is shown — it is written for an administrator
    // and says more than "something went wrong" — but never a stack trace.
    return shell(
      <AlertTriangle className="h-5 w-5 text-amber-500" />,
      `The ${noun} could not be loaded`,
      error?.message && error.message !== 'Request failed'
        ? error.message
        : 'The server did not answer. Your records are not affected.',
      onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
          <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Try again
        </Button>
      ),
    );
  }

  if (state === 'forbidden') {
    return shell(
      <Lock className="h-5 w-5 text-slate-400" />,
      `You do not have permission to view these ${noun}`,
      'The records exist. Ask an administrator to grant you access to this school.',
      null,
    );
  }

  if (state === 'signed-out') {
    return shell(
      <LogIn className="h-5 w-5 text-slate-400" />,
      'Your session has expired',
      'Sign in again to carry on.',
      <Button size="sm" variant="outline" onClick={() => window.location.assign('/login')}
              data-testid={`${testId}-signin`}>
        Sign in
      </Button>,
    );
  }

  if (state === 'filtered-out') {
    return shell(
      <FilterX className="h-5 w-5 text-slate-400" />,
      `No ${noun} match these filters`,
      // Naming the filters is the point. The live case that prompted this was
      // a campus selection with no active students behind it: the office read
      // "No students found" as 129 missing records, when one dropdown was
      // excluding every one of them.
      filterSummary?.length
        ? `Narrowed by ${filterSummary.join(', ')}. There are records here — none fit that.`
        : 'There are records here, but none of them fit what you have selected.',
      onClear && (
        <Button size="sm" variant="outline" onClick={onClear} data-testid={`${testId}-clear`}>
          <FilterX className="mr-1.5 h-3.5 w-3.5" /> Clear filters
        </Button>
      ),
    );
  }

  return shell(
    <Inbox className="h-5 w-5 text-slate-300" />,
    `No ${noun} yet`,
    `Nothing has been added to this school's ${noun} list.`,
    null,
  );
};

/** The same six states inside a table body, so the columns do not collapse. */
export const ListStateRow = ({ colSpan = 8, ...props }) => {
  const state = listStateOf(props);
  if (state === 'ready') return null;
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <ListState {...props} compact />
      </td>
    </tr>
  );
};

export default ListState;
